// src/modules/auth/pages/AuthLandingPage.tsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  styled,
  Grid,
  Icon,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(6),
  width: "100%",
  maxWidth: 1200,
  backdropFilter: "blur(20px)",
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    zIndex: 0,
  },
}));

const features = [
  {
    icon: "point_of_sale",
    title: "Smart POS",
    description: "Powerful point of sale system with advanced features",
  },
  {
    icon: "analytics",
    title: "Real-time Analytics",
    description: "Get instant insights into your business performance",
  },
  {
    icon: "inventory_2",
    title: "Inventory Control",
    description: "Manage your stock efficiently with automated tools",
  },
  {
    icon: "payments",
    title: "Secure Payments",
    description: "Multiple payment options with top-notch security",
  },
];

export const AuthLandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: 2, md: 4 },
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('/auth-pattern.svg')",
          opacity: 0.1,
          backgroundSize: "cover",
        },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <StyledPaper>
            <Grid container spacing={4}>
              {/* Left Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Typography
                      variant="h2"
                      gutterBottom
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2.5rem", md: "3.5rem" },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 3,
                      }}
                    >
                      Transform Your Business
                    </Typography>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: "text.secondary", mb: 4, lineHeight: 1.6 }}
                    >
                      Join thousands of businesses using our modern POS solution
                      to streamline operations and boost growth.
                    </Typography>
                  </motion.div>

                  <Box sx={{ display: "flex", gap: 2, mb: 6 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate("/auth/register")}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: "1.1rem",
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease-in-out",
                      }}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate("/auth/login")}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: "1.1rem",
                        borderRadius: 2,
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease-in-out",
                      }}
                    >
                      Sign In
                    </Button>
                  </Box>

                  <Grid container spacing={3}>
                    {features.map((feature, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              }}
                            >
                              <Icon
                                sx={{
                                  color: "primary.main",
                                  fontSize: 24,
                                }}
                              >
                                {feature.icon}
                              </Icon>
                            </Box>
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                              >
                                {feature.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {feature.description}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              {/* Right Section - Image */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/illustrations/pos-preview.svg"
                    alt="POS System Preview"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxWidth: 600,
                      filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.1))",
                      transform: "scale(1.1)",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.12)",
                      },
                    }}
                  />
                </motion.div>
              </Grid>
            </Grid>
          </StyledPaper>
        </motion.div>
      </Container>
    </Box>
  );
};
